import React, { useState } from 'react';
import { Plus, LayoutGrid, Star, Trash2, UsersRound, Tag, Share2, Check, X, Pencil, FileSignature } from 'lucide-react';
import { WORKSPACE_LABEL } from '../config/brand';

const LABEL_COLORS = ['#0F766E', '#4460F7', '#E5387B', '#F5B402', '#7C5CFF', '#0AA24F', '#2D8DF0', '#B7791F'];

const SidebarItem = ({ icon: Icon, label, active, onClick, muted, color, testId, badge, dropTarget, onDrop, onDragOver, onDragLeave, children }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    onDrop={onDrop}
    onDragOver={onDragOver}
    onDragLeave={onDragLeave}
    className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[14px] font-medium transition-colors ${
      dropTarget ? 'bg-[#E6F4F2] ring-2 ring-[#0F766E] ring-inset text-[#0A1551]' :
      active ? 'bg-[#F0F1FB] text-[#0A1551]' : muted ? 'text-[#6F76A7] hover:bg-[#F5F6FB] hover:text-[#0A1551]' : 'text-[#0A1551] hover:bg-[#F5F6FB]'
    }`}
  >
    {Icon && <Icon className="w-4 h-4 shrink-0" style={color ? { color } : undefined} />}
    <span className="truncate text-left flex-1">{label}</span>
    {badge != null && badge > 0 && <span className="text-[11px] font-semibold text-[#6F76A7] bg-[#F0F1FB] px-1.5 rounded-full">{badge}</span>}
    {children}
  </button>
);

const SectionHeader = ({ title, onAdd, addTestId }) => (
  <div className="flex items-center justify-between px-2.5 pt-4 pb-1.5 group">
    <span className="text-[12px] font-semibold uppercase tracking-wider text-[#6F76A7]">{title}</span>
    {onAdd && (
      <button onClick={onAdd} data-testid={addTestId} className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-[#F0F1FB] transition-opacity" aria-label={`Add to ${title}`}>
        <Plus className="w-3.5 h-3.5 text-[#6F76A7]" />
      </button>
    )}
  </div>
);

const LabelEditor = ({ initial, onSave, onCancel }) => {
  const [name, setName] = useState(initial?.name || '');
  const [color, setColor] = useState(initial?.color || LABEL_COLORS[0]);
  const save = () => { if (name.trim()) onSave({ name: name.trim(), color }); };
  return (
    <div className="px-2 py-2 rounded-md border border-[#D5D7ED] bg-white space-y-2" data-testid="label-editor">
      <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') onCancel(); }} placeholder="Label name" data-testid="label-name-input"
        className="w-full px-2 py-1.5 border border-[#D5D7ED] rounded text-[13px] focus:outline-none focus:border-[#0A1551]" />
      <div className="flex flex-wrap gap-1.5">
        {LABEL_COLORS.map((c) => (
          <button key={c} onClick={() => setColor(c)} aria-label={`Color ${c}`} className={`w-5 h-5 rounded-full ring-2 ring-offset-1 ${color === c ? 'ring-[#0A1551]' : 'ring-transparent'}`} style={{ backgroundColor: c }} />
        ))}
      </div>
      <div className="flex justify-end gap-1">
        <button onClick={onCancel} className="p-1.5 rounded hover:bg-[#F5F6FB]" aria-label="Cancel"><X className="w-3.5 h-3.5 text-[#6F76A7]" /></button>
        <button onClick={save} data-testid="label-save-button" disabled={!name.trim()} className="p-1.5 rounded bg-[#0A1551] text-white disabled:opacity-40" aria-label="Save label"><Check className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
};

const Sidebar = ({ onCreate, teams, onCreateTeam, currentTeam, onSelectTeam, onDeleteTeam, view, onSelectView, trashCount, currentUserId, isAdmin,
  labels, currentLabel, onSelectLabel, onCreateLabel, onUpdateLabel, onDeleteLabel, onDropOnLabel, open, onClose }) => {
  const [addingLabel, setAddingLabel] = useState(false);
  const [editingLabel, setEditingLabel] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const selectTeam = (id) => { onSelectView('all'); onSelectTeam(id); };

  const dragOver = (id) => (e) => { if (e.dataTransfer.types.includes('text/asset-id')) { e.preventDefault(); e.dataTransfer.dropEffect = 'link'; setDropTarget(id); } };
  const drop = (id) => (e) => { e.preventDefault(); setDropTarget(null); const assetId = e.dataTransfer.getData('text/asset-id'); if (assetId) onDropOnLabel(assetId, id); };

  return (
    <>
    {open && <div className="md:hidden fixed inset-0 bg-[#0A1551]/40 z-40" onClick={onClose} data-testid="sidebar-overlay" />}
    <aside className={`w-[250px] shrink-0 h-full bg-white border-r border-[#E3E5F5] flex flex-col fixed md:static inset-y-0 left-0 z-50 transition-transform duration-200 ${open ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`} data-testid="sidebar">
      <div className="p-3 flex items-center gap-2">
        <button onClick={onCreate} data-testid="create-button" className="flex-1 flex items-center justify-center gap-2 bg-[#0F766E] hover:bg-[#0B5F58] text-white text-[15px] font-semibold py-2.5 rounded-md transition-colors shadow-sm">
          <Plus className="w-5 h-5" strokeWidth={2.5} />
          CREATE
        </button>
        <button onClick={onClose} className="md:hidden p-2 rounded-md hover:bg-[#F5F6FB]" aria-label="Close menu" data-testid="sidebar-close"><X className="w-5 h-5 text-[#6F76A7]" /></button>
      </div>

      <div className="flex-1 overflow-y-auto px-2 pb-4">
        <SectionHeader title={WORKSPACE_LABEL} />
        <div className="space-y-0.5">
          <SidebarItem icon={LayoutGrid} label="All" testId="sidebar-all" active={view === 'all' && !currentTeam} onClick={() => selectTeam(null)} />
          <SidebarItem icon={Share2} label="Shared with Me" testId="sidebar-shared" active={view === 'shared'} onClick={() => onSelectView('shared')} />
          <SidebarItem icon={Star} label="Favorites" testId="sidebar-favorites" active={view === 'favorites'} onClick={() => onSelectView('favorites')} />
        </div>

        <SectionHeader title="Labels" onAdd={() => setAddingLabel(true)} addTestId="sidebar-add-label-header" />
        <div className="space-y-0.5">
          {labels.map((l) => editingLabel === l.id ? (
            <LabelEditor key={l.id} initial={l} onCancel={() => setEditingLabel(null)} onSave={(p) => { onUpdateLabel(l.id, p); setEditingLabel(null); }} />
          ) : (
            <div key={l.id} className="group relative">
              <SidebarItem icon={Tag} label={l.name} color={l.color} testId={`sidebar-label-${l.id}`} active={view === 'label' && currentLabel === l.id}
                onClick={() => onSelectLabel(l.id)} dropTarget={dropTarget === l.id} onDragOver={dragOver(l.id)} onDragLeave={() => setDropTarget(null)} onDrop={drop(l.id)} />
              <div className="absolute right-1 top-1/2 -translate-y-1/2 hidden group-hover:flex items-center">
                <button onClick={(e) => { e.stopPropagation(); setEditingLabel(l.id); }} className="p-1 rounded hover:bg-[#F0F1FB]" aria-label={`Edit ${l.name}`} data-testid={`label-edit-${l.id}`}><Pencil className="w-3 h-3 text-[#6F76A7]" /></button>
                <button onClick={(e) => { e.stopPropagation(); onDeleteLabel(l.id); }} className="p-1 rounded hover:bg-[#FEF2F2]" aria-label={`Delete ${l.name}`} data-testid={`label-delete-${l.id}`}><Trash2 className="w-3 h-3 text-[#E5387B]" /></button>
              </div>
            </div>
          ))}
          {addingLabel ? (
            <LabelEditor onCancel={() => setAddingLabel(false)} onSave={(p) => { onCreateLabel(p); setAddingLabel(false); }} />
          ) : (
            <SidebarItem icon={Plus} label="Add label" muted testId="sidebar-add-label" onClick={() => setAddingLabel(true)} />
          )}
          {labels.length > 0 && <p className="px-2.5 pt-1 text-[11px] text-[#9599C0]">Drag an asset onto a label to tag it.</p>}
        </div>

        <SectionHeader title="Team Workspaces" onAdd={onCreateTeam} />
        <div className="space-y-0.5">
          {teams.map((t) => (
            <div key={t.id} className="group relative">
              <SidebarItem icon={UsersRound} label={t.name} color={t.color} testId={`sidebar-team-${t.id}`} active={view === 'all' && currentTeam === t.id} onClick={() => selectTeam(t.id)} />
              {t.owner_id === currentUserId && (
                <button onClick={(e) => { e.stopPropagation(); onDeleteTeam(t.id); }} className="absolute right-1.5 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-[#FEF2F2]" aria-label={`Delete ${t.name}`}>
                  <Trash2 className="w-3.5 h-3.5 text-[#E5387B]" />
                </button>
              )}
            </div>
          ))}
          <SidebarItem icon={UsersRound} label="Create team" muted testId="sidebar-create-team" onClick={onCreateTeam} />
        </div>

        <div className="mt-6 space-y-0.5 border-t border-[#E3E5F5] pt-4">
          <SidebarItem icon={Trash2} label="Trash" testId="sidebar-trash" active={view === 'trash'} onClick={() => onSelectView('trash')} badge={trashCount} />
          {isAdmin && <SidebarItem icon={FileSignature} label="Investor Agreements" testId="sidebar-agreements" active={view === 'agreements'} onClick={() => onSelectView('agreements')} />}
        </div>
      </div>
    </aside>
    </>
  );
};

export default Sidebar;
